import React, { useState } from 'react';
import { LogoMark, Ornament } from './graphics/Graphics';
import { PolicyModal } from './PolicyModal';
type PolicyKey = 'about' | 'contact' | 'shipping' | 'privacy' | 'terms' | null;
export function Footer() {
  const [openPolicy, setOpenPolicy] = useState<PolicyKey>(null);
  const policies: {
    key: Exclude<PolicyKey, null>;
    label: string;
    title: string;
  }[] = [{
    key: 'about',
    label: 'About',
    title: 'About DryControl™'
  }, {
    key: 'contact',
    label: 'Contact',
    title: 'Contact Us'
  }, {
    key: 'shipping',
    label: 'Shipping & Returns',
    title: 'Shipping & Returns'
  }, {
    key: 'privacy',
    label: 'Privacy Policy',
    title: 'Privacy Policy'
  }, {
    key: 'terms',
    label: 'Terms & Conditions',
    title: 'Terms & Conditions'
  }];
  return <footer className="bg-ink py-10 md:py-12 px-5" data-id="element-136">
      <div className="max-w-[1200px] mx-auto" data-id="element-137">
        <div className="flex justify-center mb-8" data-id="element-138">
          <Ornament color="#C9A96E" width={80} className="opacity-50" data-id="element-139" />
        </div>
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-10" data-id="element-140">
          {/* Logo */}
          <div className="flex items-center gap-2" data-id="element-141">
            <LogoMark className="w-6 h-6 text-gold" data-id="element-142" />
            <div className="font-playfair text-[18px] text-white font-semibold tracking-tight" data-id="element-143">
              DryControl™
            </div>
          </div>

          {/* Links */}
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 font-inter text-[13px] text-white/60" data-id="element-144">
            {policies.map(p => <button key={p.key} onClick={() => setOpenPolicy(p.key)} className="hover:text-white transition-colors cursor-pointer" data-id="element-145">
                {p.label}
              </button>)}
          </div>

          {/* Trust Badges */}
          <div className="flex items-center space-x-4 font-inter text-[12px] text-white/60" data-id="element-146">
            <span className="flex items-center" data-id="element-147">🔒 SSL Secure</span>
            <span className="text-white/20" data-id="element-148">|</span>
            <span data-id="element-149">🇬🇧 UK Registered Company</span>
          </div>
        </div>

        <div className="w-full h-[1px] bg-white/10 mb-8" data-id="element-150"></div>

        {/* Disclaimer */}
        <div className="font-inter text-[12px] text-white/40 text-center max-w-[800px] mx-auto leading-relaxed" data-id="element-151">
          <p className="mb-2" data-id="element-152">© 2026 DryControl. All rights reserved.</p>
          <p data-id="element-153">
            This page contains the experiences of real customers. Individual
            results may vary. DryControl is not a medical device and does not
            diagnose, treat, cure, or prevent any medical condition. If you have
            concerns about your bladder health, please consult a qualified
            healthcare professional.
          </p>
        </div>
      </div>

      {/* Policy Modals */}
      <PolicyModal isOpen={openPolicy === 'about'} onClose={() => setOpenPolicy(null)} title="About DryControl™" data-id="element-154">
        <p className="mb-4" data-id="element-155">
          DryControl™ is a UK-based women's health brand built around a single,
          stubborn idea: the standard advice given to women over fifty about
          bladder leakage is not nearly good enough.
        </p>
        <p className="mb-4" data-id="element-156">
          Our founding team — clinicians, formulators, and the daughters of
          mothers who quietly suffered for decades — set out to develop a
          product that addresses the cause, not just the consequence. Six years
          of research and consultation with NHS continence specialists later,
          DryControl™ is the result.
        </p>
        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-157">
          Our mission
        </h3>
        <p className="mb-4" data-id="element-158">
          To restore freedom, dignity, and confidence to the millions of British
          women who have been told that leaking is "just part of getting older."
          It isn't. And we're here to prove it.
        </p>
        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-159">
          Where we are
        </h3>
        <p className="mb-2" data-id="element-160">
          DryControl Ltd. is a UK-registered company (Company No. 14823091),
          headquartered in Manchester, with formulation and quality assurance
          carried out at our partner facility in Cheshire. All orders are
          dispatched from our UK fulfilment centre.
        </p>
      </PolicyModal>

      <PolicyModal isOpen={openPolicy === 'contact'} onClose={() => setOpenPolicy(null)} title="Contact Us" data-id="element-161">
        <p className="mb-4" data-id="element-162">
          We are a small team and we read every email personally. Most queries
          receive a reply within one working day; complex questions (refunds,
          medical concerns) may take up to two.
        </p>

        <div className="bg-cream rounded-xl p-5 my-6 border border-divider" data-id="element-163">
          <div className="font-inter font-semibold text-[14px] text-forest uppercase tracking-wider mb-3" data-id="element-164">
            Customer Care
          </div>
          <p className="mb-2" data-id="element-165">
            <strong data-id="element-166">Email:</strong>{' '}
            <a href="mailto:hello@drycontrol.co.uk" className="text-forest underline" data-id="element-167">
              hello@drycontrol.co.uk
            </a>
          </p>
          <p className="mb-2" data-id="element-168">
            <strong data-id="element-169">Phone:</strong> 0800 802 1183 (Mon–Fri, 9am–5pm GMT)
          </p>
          <p data-id="element-170">
            <strong data-id="element-171">Post:</strong> DryControl Ltd., 12 St. Ann's Square,
            Manchester, M2 7HW, United Kingdom
          </p>
        </div>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-172">
          Press &amp; partnerships
        </h3>
        <p className="mb-4" data-id="element-173">
          For media enquiries, please contact{' '}
          <a href="mailto:press@drycontrol.co.uk" className="text-forest underline" data-id="element-174">
            press@drycontrol.co.uk
          </a>
          .
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-175">
          Healthcare professionals
        </h3>
        <p data-id="element-176">
          GPs, continence nurses, and pelvic-health physiotherapists requesting
          ingredient lists or clinical summaries can write to{' '}
          <a href="mailto:clinical@drycontrol.co.uk" className="text-forest underline" data-id="element-177">
            clinical@drycontrol.co.uk
          </a>
          .
        </p>
      </PolicyModal>

      <PolicyModal isOpen={openPolicy === 'shipping'} onClose={() => setOpenPolicy(null)} title="Shipping & Returns" data-id="element-178">
        <h3 className="font-playfair font-semibold text-[18px] text-ink mb-2" data-id="element-179">
          UK delivery
        </h3>
        <p className="mb-4" data-id="element-180">
          We ship every order from our Manchester fulfilment centre by Royal
          Mail Tracked 48. Standard UK delivery is{' '}
          <strong data-id="element-181">free on every order</strong>, regardless of bundle size, and
          typically arrives within 2–3 working days of dispatch.
        </p>
        <p className="mb-4" data-id="element-182">
          Orders placed before 2pm GMT on a working day are dispatched the same
          day. Orders placed after 2pm or at weekends are dispatched on the next
          working day.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-183">
          Discreet packaging
        </h3>
        <p className="mb-4" data-id="element-184">
          Every parcel arrives in a plain, unbranded outer mailer. There is no
          mention of DryControl, no medical references, and no indication of
          contents on the exterior. Your privacy is non-negotiable.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-185">
          International orders
        </h3>
        <p className="mb-4" data-id="element-186">
          We currently ship within the United Kingdom, the Channel Islands, and
          the Republic of Ireland only. Mainland Europe and the rest of the
          world will be added in late 2026.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-187">
          90-day money-back guarantee
        </h3>
        <p className="mb-4" data-id="element-188">
          If you are not completely satisfied with DryControl™ within 90 days
          of receiving your order, simply email us at{' '}
          <a href="mailto:hello@drycontrol.co.uk" className="text-forest underline" data-id="element-189">
            hello@drycontrol.co.uk
          </a>{' '}
          and we will refund the full purchase price — including any boxes you
          have already opened or used. You do not need to return the empty
          packaging.
        </p>
        <p className="mb-4" data-id="element-190">
          Refunds are issued to the original payment method within 5 working
          days of receipt of your refund request.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-191">
          Damaged or incorrect items
        </h3>
        <p data-id="element-192">
          If your order arrives damaged or you have received the wrong items,
          please email{' '}
          <a href="mailto:hello@drycontrol.co.uk" className="text-forest underline" data-id="element-193">
            hello@drycontrol.co.uk
          </a>{' '}
          within 14 days with a photograph and your order number. We will
          dispatch a replacement free of charge.
        </p>
      </PolicyModal>

      <PolicyModal isOpen={openPolicy === 'privacy'} onClose={() => setOpenPolicy(null)} title="Privacy Policy" data-id="element-194">
        <p className="mb-4 text-secondary text-[13px]" data-id="element-195">
          Last updated: 1 May 2026
        </p>
        <p className="mb-4" data-id="element-196">
          DryControl Ltd. ("we", "us", "our") is committed to protecting and
          respecting your privacy. This policy explains what personal data we
          collect, how we use it, and your rights under the UK General Data
          Protection Regulation (UK GDPR) and the Data Protection Act 2018.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-197">
          1. What we collect
        </h3>
        <p className="mb-2" data-id="element-198">
          When you place an order or contact us, we may collect:
        </p>
        <ul className="list-disc pl-6 mb-4 space-y-1" data-id="element-199">
          <li data-id="element-200">
            Your name, delivery address, email address, and telephone number
          </li>
          <li data-id="element-201">
            Payment details (processed securely by Stripe; we never store full
            card numbers)
          </li>
          <li data-id="element-202">Order history and any correspondence you send to us</li>
          <li data-id="element-203">Anonymous browsing analytics via cookies (see Section 5)</li>
        </ul>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-204">
          2. How we use it
        </h3>
        <p className="mb-2" data-id="element-205">We use your data only for:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1" data-id="element-206">
          <li data-id="element-207">Fulfilling and dispatching your orders</li>
          <li data-id="element-208">Responding to enquiries and processing refunds</li>
          <li data-id="element-209">
            Sending order confirmations and (only with your consent) occasional
            product updates
          </li>
          <li data-id="element-210">Improving our website and customer experience</li>
        </ul>
        <p className="mb-4" data-id="element-211">
          We will <strong data-id="element-212">never</strong> sell your data to third parties.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-213">
          3. Who we share data with
        </h3>
        <p className="mb-4" data-id="element-214">
          Your data is shared only with carefully selected service providers who
          help us run our business — payment processors (Stripe), email
          providers (Klaviyo), and shipping carriers (Royal Mail). All are UK
          GDPR compliant.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-215">
          4. How long we keep it
        </h3>
        <p className="mb-4" data-id="element-216">
          Order records are retained for 7 years to satisfy HMRC requirements.
          Marketing data is held only while you remain subscribed; you can
          unsubscribe at any time using the link in any email.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-217">
          5. Cookies
        </h3>
        <p className="mb-4" data-id="element-218">
          We use a minimal set of cookies: essential cookies (cart, checkout)
          and anonymous analytics cookies via Google Analytics 4. No advertising
          or cross-site tracking cookies are placed without your explicit
          consent.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-219">
          6. Your rights
        </h3>
        <p className="mb-2" data-id="element-220">Under UK GDPR you have the right to:</p>
        <ul className="list-disc pl-6 mb-4 space-y-1" data-id="element-221">
          <li data-id="element-222">Access a copy of the data we hold on you</li>
          <li data-id="element-223">Correct inaccurate data</li>
          <li data-id="element-224">Request erasure ("right to be forgotten")</li>
          <li data-id="element-225">Object to processing or withdraw consent at any time</li>
          <li data-id="element-226">
            Lodge a complaint with the Information Commissioner's Office
            (ico.org.uk)
          </li>
        </ul>
        <p data-id="element-227">
          To exercise any of these, email{' '}
          <a href="mailto:privacy@drycontrol.co.uk" className="text-forest underline" data-id="element-228">
            privacy@drycontrol.co.uk
          </a>
          .
        </p>
      </PolicyModal>

      <PolicyModal isOpen={openPolicy === 'terms'} onClose={() => setOpenPolicy(null)} title="Terms & Conditions" data-id="element-229">
        <p className="mb-4 text-secondary text-[13px]" data-id="element-230">
          Last updated: 1 May 2026
        </p>
        <p className="mb-4" data-id="element-231">
          These terms govern your use of the drycontrol.co.uk website and the
          purchase of any products from DryControl Ltd. By placing an order you
          confirm that you have read and accepted these terms.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-232">
          1. Medical disclaimer
        </h3>
        <p className="mb-4" data-id="element-233">
          DryControl™ is a wellbeing product. It is not a medical device and is
          not intended to diagnose, treat, cure, or prevent any disease or
          medical condition. The information presented on this website is for
          general educational purposes only and is not a substitute for advice
          from a qualified healthcare professional. If you have ongoing
          incontinence, are pregnant, breastfeeding, taking prescription
          medication, or have any underlying medical condition, please consult
          your GP before using DryControl™.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-234">
          2. Orders and pricing
        </h3>
        <p className="mb-4" data-id="element-235">
          All prices are listed in pounds sterling (£) and include UK VAT where
          applicable. We reserve the right to amend prices at any time, but
          changes will not affect orders already placed. Promotional discounts
          ("Buy 2 Get 2 Free", etc.) apply at the point of sale only and cannot
          be combined.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-236">
          3. Payment
        </h3>
        <p className="mb-4" data-id="element-237">
          Payment is processed at checkout via Stripe and is fully encrypted. We
          accept all major debit and credit cards, Apple Pay, and PayPal.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-238">
          4. Cancellation and refunds
        </h3>
        <p className="mb-4" data-id="element-239">
          Under the Consumer Contracts Regulations 2013 you may cancel any
          unopened order within 14 days of receipt for a full refund. Beyond
          this, our 90-day money-back guarantee applies even to opened and used
          product — see our Shipping &amp; Returns policy.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-240">
          5. Subscriptions
        </h3>
        <p className="mb-4" data-id="element-241">
          DryControl™ does <strong data-id="element-242">not</strong> operate any auto-shipping or
          subscription model. Every order is a one-time purchase. We will never
          charge you again unless you place a new order yourself.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-243">
          6. Intellectual property
        </h3>
        <p className="mb-4" data-id="element-244">
          All text, images, logos, and design elements on this site are the
          property of DryControl Ltd. and may not be reproduced without written
          permission.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-245">
          7. Limitation of liability
        </h3>
        <p className="mb-4" data-id="element-246">
          To the fullest extent permitted by law, our liability for any claim
          arising from the purchase or use of DryControl™ is limited to the
          amount you paid for the relevant order. Nothing in these terms
          excludes liability for death, personal injury, or fraudulent
          misrepresentation.
        </p>

        <h3 className="font-playfair font-semibold text-[18px] text-ink mt-6 mb-2" data-id="element-247">
          8. Governing law
        </h3>
        <p data-id="element-248">
          These terms are governed by the laws of England and Wales. Any
          disputes will be subject to the exclusive jurisdiction of the English
          courts.
        </p>
      </PolicyModal>
    </footer>;
}